import React, {useEffect, useState} from 'react';
import Checkbox from '@material-ui/core/Checkbox';
import {useHistory} from 'react-router-dom';
import {curry} from 'ramda';
import wrap from 'wraptile';
import moment from 'moment';
import {useDebouncedCallback} from 'use-debounce';
import {OnStringSetState} from '@pluto/@types';
import getStatusIcon from '@pluto/qc-job-status-icon';
import {handleAutocompleteChange} from '@pluto/autocomplete';
import {formatDateToServer} from '@pluto/react-select-mapping';
import {renderAssignedDate} from '@pluto/utils';
import {
    ColumnType,
    Sort,
    getInitialSort,
    getInitialPage,
    getInitialRowsPerPage,
    SortingOptionsValues,
} from '@pluto/table';
import useQuery from '@pluto/query-hook';
import {UserValue} from '@pluto/users';
import {getOptsFromQuery, handleSelect} from '@pluto/select';
import {getInitialRanges} from '@pluto/daterange-picker';
import {DateRangeState} from '@pluto/daterange-picker/DateRangeTypes';
import {setQuery} from '@pluto/set-history-query';
import {handleSort} from '@pluto/qc-sort';
import {handleQcManagerList} from '@pluto/qc-paging';
import {
    handleDebouncedCallback,
    handleDebounce,
} from '@pluto/qc-debounce';
import {handlePreselect} from '@pluto/qc-preselect';
import {handleMount} from '@pluto/qc-mount';
import {makeHandleFilters} from '@pluto/qc-handle-filters';
import {handleDateRangeChange} from '@pluto/qc-handle-daterange';
import {renderQCReportLink} from 'QCWorkflow/QCJobs/QCJobsContainer';
import {baseRoutes} from '../../App/AppRouter/routes';
import QCManager from './QCManager';
import QCManagerColumns from './QCManagerStaticColumns';
import {
    QCFiltersParams,
    HandlePreselectChange,
    FilterHandlers,
    HandleChangeInputType,
    FiltersKeys,
    HandleChangeCheckboxParams,
    Priorities,
    SelectChangeEvent,
    SetPriorityLevel,
    ExportQCAction,
    SetAssignedUser,
    SetModalOpen,
    QCJobItemModified,
    QCJobItem,
    RenderPriorityPopupProps,
    QCManagerContainerProps,
    HandlePriorityChange,
    HandleUserAssign,
    HandleUserUnassign,
    RenderAssignLink,
    SetOpen,
    HandleMultipleDueDateChangeParams,
} from './QCManagerTypes';
import {
    QCSpecificFilters,
    QCFilters,
} from '../QCFilters/QCFiltersType';
import AssignDialogContainer, {getIsAssigned} from './QCManagerDialogs/AssignQCModal/AssignDialogContainer';
import DueDateQCModalContainer from './QCManagerDialogs/DueDateQCModal/DueDateQCModalContainer';
import PriorityQCModalContainer from './QCManagerDialogs/PriorityQCModal/PriorityQCModalContainer';
import {UnAssignIdObj} from './Redux/QCManagerActions';

export const renderStatusIcon = curry((item: QCJobItem) => {
    return getStatusIcon(item);
});

export const renderAssignLink = wrap((handlers: RenderAssignLink, rows: QCJobItemModified[], item: QCJobItem) => {
    const {
        handleSelectChangeAssign,
        unassignQCJob,
        assignQCJob,
        params,
        setAssignedUser,
        assignedUser,
    } = handlers;
    const {
        id,
        assignee,
    } = item;

    return (
        <AssignDialogContainer
            data={rows}
            handleSelectChangeAssign={handleSelectChangeAssign}
            setAssignedUser={setAssignedUser}
            assignQCJob={assignQCJob}
            unassignQCJob={unassignQCJob}
            id={id}
            assignedUser={assignedUser}
            assignee={assignee}
            params={params}
        />
    );
});

export const handleFilterCheckedItems = curry((id: number, item: number) => item !== id);

export const handleChangeCheckbox = wrap((params: HandleChangeCheckboxParams, id: number) => {
    const {
        checkedItems,
        setCheckedItems,
    } = params;

    if (!checkedItems.includes(id))
        return setCheckedItems([
            id,
            ...checkedItems,
        ]);

    setCheckedItems(checkedItems.filter(handleFilterCheckedItems(id)));
});

export const handleChangeSelectAllCheckbox = wrap((params: HandleChangeCheckboxParams, allJobsItemsIds: number[]) => {
    const {
        checkedItems,
        setCheckedItems,
    } = params;

    if (checkedItems.length !== allJobsItemsIds.length) {
        return setCheckedItems(allJobsItemsIds);
    }
    setCheckedItems([]);
});

export const renderCheckbox = curry((params: HandleChangeCheckboxParams, item: QCJobItemModified) => {
    const {
        checkedItems,
        setCheckedItems,
    } = params;
    const {id} = item;
    const onChangeCheckbox = handleChangeCheckbox({
        checkedItems,
        setCheckedItems,
    }, id);

    return (
        <Checkbox
            className="priority-select"
            checked={checkedItems.includes(id)}
            onChange={onChangeCheckbox}
        />
    );
});

export const isCheckedAllJobs = (checkedItems: number[], allJobsItemsIds: number[]): boolean => {
    return checkedItems.length > 0 && checkedItems.length === allJobsItemsIds.length;
};

export const renderSelectAllCheckbox = (params: HandleChangeCheckboxParams, allJobsItemsIds: number[]) => {
    const {checkedItems} = params;
    const onChangeCheckbox = handleChangeSelectAllCheckbox(params, allJobsItemsIds);

    return (
        <Checkbox
            className="priority-select-all"
            checked={isCheckedAllJobs(checkedItems, allJobsItemsIds)}
            onChange={onChangeCheckbox}
        />
    );
};

export const renderDueDatePopupLink = wrap((handlers: any, item: QCJobItem): JSX.Element => {
    const {
        setQcJobDueDate,
        setNotification,
        params,
    } = handlers;
    const {
        id,
        dueDate,
    } = item;

    return (
        <DueDateQCModalContainer
            setQcJobDueDate={setQcJobDueDate}
            setNotification={setNotification}
            params={params}
            jobId={id}
            dueDate={dueDate}
        />
    );
});

export const renderPriorityPopupLink = wrap((handlers: RenderPriorityPopupProps, item: QCJobItem): JSX.Element => {
    const {
        changeQCJobPriority,
        params,
    } = handlers;
    const {
        id,
        priority,
        contentItem,
    } = item;
    const {title} = contentItem;

    return (
        <PriorityQCModalContainer
            jobId={id}
            title={title}
            priority={priority}
            changeQCJobPriority={changeQCJobPriority}
            params={params}
        />
    );
});

export const handleModalChange = wrap((setModalOpen: SetModalOpen) => {
    setModalOpen(true);
});

export const formPriority = curry((priority, id) => ({
    id,
    priority,
}));

export const handlePriorityChange = wrap((handlers: HandlePriorityChange) => {
    const {
        checkedItems,
        setPriorityModalOpen,
        priorityLevel,
        changeQCJobPriority,
        setCheckedItems,
        params,
    } = handlers;
    const withPriority = formPriority(priorityLevel);
    const body = checkedItems.map(withPriority);
    changeQCJobPriority(body, params);
    setPriorityModalOpen(false);
    setCheckedItems([]);
});

export const handleSelectChangePriority = curry((setPriorityLevel: SetPriorityLevel, e: SelectChangeEvent) => {
    setPriorityLevel(e.target.value as Priorities);
});

export const handleExport = wrap((params: QCFiltersParams, exportQCJobs: ExportQCAction) => {
    exportQCJobs(params);
});

export const handleModalClose = wrap((setModalOpen: SetModalOpen, setAssignedUser: OnStringSetState) => {
    setAssignedUser('');
    setModalOpen(false);
});

export const handlePriorityPopupClose = wrap((setPriorityModalOpen: SetOpen) => {
    setPriorityModalOpen(false);
});

export const formatCheckedItems = curry((userId: number, id: number) => ({
    id,
    userId,
}));

export const handleUserAssign = wrap((handlers: HandleUserAssign) => {
    const {
        checkedItems,
        assignedUser,
        setAssignModalOpen,
        params,
        assignQCJob,
        setCheckedItems,
        setAssignedUser,
    } = handlers;

    if (assignedUser && checkedItems.length) {
        const {id} = assignedUser;
        const withIds = formatCheckedItems(id);
        const body = checkedItems.map(withIds);

        assignQCJob(body, params);
        setAssignModalOpen(false);
        setAssignedUser('');
        setCheckedItems([]);
    }
});

export const idToObj = (id: number): UnAssignIdObj => ({
    id,
    userId: null,
});

export const handleUserUnassign = wrap((handlers: HandleUserUnassign) => {
    const {
        checkedItems,
        setAssignModalOpen,
        params,
        unassignQCJob,
        setCheckedItems,
    } = handlers;

    if (!checkedItems.length)
        return;

    const body = checkedItems.map(idToObj);

    unassignQCJob(body, params);
    setAssignModalOpen(false);
    setCheckedItems([]);
});

export const getInitialQCManagerSort = (query: URLSearchParams): Sort => {
    const initialSort = getInitialSort(query);

    if (initialSort.sortBy) {
        return initialSort;
    }

    return {
        sortBy: 'dueDate',
        sortOrder: SortingOptionsValues.desc,
    };
};

export const handleSelectChangeAssign = curry((setAssignedUser: SetAssignedUser, user: UserValue) => {
    setAssignedUser(user);
});

const handleIsChecked = curry((priorityLevel: number, priorityValue: Priorities) => {
    return Number(priorityLevel) === priorityValue;
});

export const renderQCManagerReportLink = (item: QCJobItemModified): JSX.Element => {
    const rootId = 'qc-manager';

    return renderQCReportLink(item, rootId);
};

export const handleDueDateMultipleJobChange = curry((handlers: HandleMultipleDueDateChangeParams, dateValue: string) => {
    const {
        checkedItems,
        params,
        setQcJobDueDate,
        setCheckedItems,
    } = handlers;
    const body = checkedItems.map((id: number) => ({
        id,
        dueDate: formatDateToServer(dateValue),
    }));

    setQcJobDueDate(body, params);
    setCheckedItems([]);
});

export const getParamDates = (dates: DateRangeState) => ({
    start:  dates ? moment(dates.start).format('DD.MM.YY') : '',
    end:  dates ? moment(dates.end).format('DD.MM.YY') : ''
});

export const setSearchParams = wrap((query: URLSearchParams) => sessionStorage.setItem('rootQuery', query.toString()));

export default function QCManagerContainer(props: QCManagerContainerProps) {
    const {
        setQCJobs,
        resetQCJobs,
        exportQCJobs,
        changeQCJobPriority,
        assignQCJob,
        unassignQCJob,
        getQCJobsSummary,
        setQcJobDueDate,
        qcManager,
        setNotification,
    } = props;
    const {total, rows} = qcManager;
    const query = useQuery();
    const getFromQuery = getOptsFromQuery(query);
    const history = useHistory();
    const initialRowsPerPage = getInitialRowsPerPage(query);
    const [rowsPerPage, setRowsPerPage] = useState<number>(initialRowsPerPage);
    const [checkedItems, setCheckedItems] = useState<number[]>([]);
    const initialPage = getInitialPage({
        query,
        rowsPerPage,
    });
    const DEBOUNCE_TIME = 700;
    const [page, setPage] = useState<number>(initialPage);
    const initialSort = getInitialQCManagerSort(query);
    const [sort, setSort] = useState<Sort>(initialSort);
    const [title, setTitle] = useState<string>(query.get('title') || '');
    const [assignModalOpen, setAssignModalOpen] = useState<boolean>(false);
    const [priorityModalOpen, setPriorityModalOpen] = useState<boolean>(false);
    const [assignedUser, setAssignedUser] = useState<UserValue>(null);
    const [priorityLevel, setPriorityLevel] = useState<Priorities>(Priorities.Medium);
    const getChecked = handleIsChecked(priorityLevel);
    const initialDueDate = getInitialRanges(query, 'dateStart', 'dateEnd');
    const initialFinishDate = getInitialRanges(query, 'finishDateStart', 'finishDateEnd');
    const [dueDates, setDueDates] = useState<DateRangeState>(initialDueDate);
    const [finishDates, setFinishDates] = useState<DateRangeState>(initialFinishDate);
    const [filters, setFilters] = useState<QCSpecificFilters>({
        qcJobType: query.get('qcJobType') || '',
        contentPartnerId: getFromQuery('contentPartnerId'),
        contentItemType: getFromQuery('contentItemType'),
        qcJobAssigneeId: getFromQuery('qcJobAssigneeId'),
        qcJobStatus: getFromQuery('qcJobStatus'),
    });

    const finishParamDates = getParamDates(finishDates);
    const dueDatesParam = getParamDates(dueDates);

    const params: QCFilters = {
        ...filters,
        ...sort,
        title,
        finishDates,
        finishDateEnd: finishParamDates.end,
        finishDateStart: finishParamDates.start,
        dates: dueDates,
        dateEnd: dueDatesParam.end,
        dateStart: dueDatesParam.start,
        offset: rowsPerPage * page,
        limit: rowsPerPage,
    };

    const onUserAssign = handleUserAssign({
        checkedItems,
        setCheckedItems,
        setAssignModalOpen,
        assignedUser,
        assignQCJob,
        params,
        setAssignedUser,
    });
    const onUserUnassign = handleUserUnassign({
        checkedItems,
        setCheckedItems,
        setAssignModalOpen,
        unassignQCJob,
        params,
    });
    const allJobsItemsIds = rows.map((row) => row.id);
    const columns: ColumnType[] = [
        {
            dataKey: '',
            title: renderSelectAllCheckbox({
                checkedItems,
                setCheckedItems,
            }, allJobsItemsIds),
            render: renderCheckbox({
                checkedItems,
                setCheckedItems,
            }),
        },
        {
            dataKey: 'priority',
            title: 'Prio',
            sortable: true,
            render: renderPriorityPopupLink({
                setPriorityLevel,
                priorityLevel,
                changeQCJobPriority,
                params,
                setCheckedItems,
            }),
        },
        {
            dataKey: 'title',
            title: 'Title',
            render: renderQCManagerReportLink,
            sortable: true,
            width: 25,
        },
        {
            dataKey: 'dueDate',
            title: 'Due date',
            render: renderDueDatePopupLink({
                setQcJobDueDate,
                setNotification,
                params,
            }),
            sortable: true,
            width: 10,
        },
        ...QCManagerColumns,
        {
            dataKey: 'assigneeName',
            title: 'Assignee',
            render: renderAssignLink({
                handleSelectChangeAssign,
                unassignQCJob,
                assignQCJob,
                params,
                setAssignedUser,
                assignedUser,
            }, rows),
            width: 7,
        },
        {
            dataKey: 'assignedAt',
            title: 'Date assigned',
            render: renderAssignedDate,
            sortable: true,
        },
        {
            dataKey: 'status',
            title: 'Status',
            render: renderStatusIcon,
            width: 8,
        },
    ];

    const debouncedCallback = handleDebouncedCallback({
        ...filters,
        ...sort,
        title,
        limit: rowsPerPage,
        offset: 0,
        setQCJobs,
        setPage,
        setQuery,
        history,
        route: baseRoutes.qcManager,
    });
    const [debouncedFunction] = useDebouncedCallback(debouncedCallback, DEBOUNCE_TIME);

    const onDebouncedSearch: HandleChangeInputType = handleDebounce({
        debouncedFunction,
        setTitle,
    });

    const filterHandlers: FilterHandlers = {
        ...sort,
        setFilters,
        setPage,
        setQuery,
        history,
        setQCJobs,
        filters,
        rowsPerPage,
        title,
        route: baseRoutes.qcManager,
        setDueDates,
        setFinishDates,
        dueDates,
        finishDates,
    };

    const onPreselectClick: HandlePreselectChange = handlePreselect(filterHandlers);

    const handleFilters = makeHandleFilters(filterHandlers);
    const handleTypeChange = handleFilters(FiltersKeys.Type);

    const handleContentPartnersChange = handleAutocompleteChange({
        ...filterHandlers,
        key: FiltersKeys.ContentPartnerId,
    });

    const handleContentTypesChange = handleAutocompleteChange({
        ...filterHandlers,
        key: FiltersKeys.ContentType,
    });

    const handleAssigneeChange = handleAutocompleteChange({
        ...filterHandlers,
        key: FiltersKeys.Assignee,
    });
    const handleStatusChange = handleSelect({
        ...filterHandlers,
        key: FiltersKeys.Status,
    });

    const onPaging = handleQcManagerList(params, {
        setQCJobs,
        setQuery,
        history,
        route: baseRoutes.qcManager,
    });

    const onMount = handleMount({
        setQCJobs,
        resetQCJobs,
        getQCJobsSummary,
        setRowsPerPage,
        setPage,
        params,
        query,
    });

    useEffect(onMount, []);

    useEffect(setSearchParams(query), [query]);

    const onSort = handleSort({
        setSort,
        setQCJobs,
        params,
        setQuery,
        history,
        route: baseRoutes.qcManager,
    });

    const onExport = handleExport({
        ...sort,
        ...filters,
        title,
    }, exportQCJobs);

    const onDueDateRangeSelect = handleDateRangeChange(filterHandlers, 'dueDate');
    const onFinishDateRangeSelect = handleDateRangeChange(filterHandlers, 'finishDate');
    const onMultiAssignSelectChange = handleSelectChangeAssign(setAssignedUser);
    const onMultipleJobDueDateChange = handleDueDateMultipleJobChange({
        checkedItems,
        params,
        setQcJobDueDate,
        setCheckedItems,
    });
    const multiPriorityChange: HandlePriorityChange = {
        checkedItems,
        setPriorityModalOpen,
        priorityLevel,
        changeQCJobPriority,
        setCheckedItems,
        setQCJobs,
        params,
    };
    return (
        <QCManager
            assignedUser={assignedUser}
            onMultipleJobDueDateChange={onMultipleJobDueDateChange}
            checkedItems={checkedItems}
            handleMultiChangePriorityModal={handleModalChange(setPriorityModalOpen)}
            handleMultiChangeAssigneeModal={handleModalChange(setAssignModalOpen)}
            handleSelectChangePriority={handleSelectChangePriority(setPriorityLevel)}
            filters={filters}
            handlePreselect={onPreselectClick}
            handleTypeChange={handleTypeChange}
            isPriorityModalOpen={priorityModalOpen}
            isAssigned={getIsAssigned(assignedUser)}
            isAssignModalOpen={assignModalOpen}
            getChecked={getChecked}
            handlePriorityClose={handlePriorityPopupClose(setPriorityModalOpen)}
            handleAssignClose={handleModalClose(setAssignModalOpen, setAssignedUser)}
            handleUserAssign={onUserAssign}
            handleUserUnAssign={onUserUnassign}
            handleSelectChangeAssign={onMultiAssignSelectChange}
            handlePriorityChange={handlePriorityChange(multiPriorityChange)}
            handleContentPartnersChange={handleContentPartnersChange}
            handleContentTypesChange={handleContentTypesChange}
            handleAssigneeChange={handleAssigneeChange}
            handleStatusChange={handleStatusChange}
            handleTitleChange={onDebouncedSearch}
            title={title}
            setPage={setPage}
            page={page}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            onPaging={onPaging}
            data={rows}
            columns={columns}
            sort={sort}
            total={total}
            setSort={onSort}
            onExport={onExport}
            dueDates={dueDates}
            finishDates={finishDates}
            onDueDateRangeSelect={onDueDateRangeSelect}
            onFinishDateRangeSelect={onFinishDateRangeSelect}
            setFinishDates={setFinishDates}
            qcManager={qcManager}
        />
    );
}
